import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertCircle,
  Clock,
  Gift,
  Plus,
  RefreshCw,
  Users
} from 'lucide-react';
import toast from 'react-hot-toast';

// Context
import { useAuth } from '../../../contexts/AuthContext';

// Components
import AdminPageBanner from '../../../components/admin/AdminPageBanner';
import UserStatsCards from './components/UserStatsCards';
import SearchAndFilters from './components/SearchAndFilters';
import UserCard from './components/UserCard';
import Pagination from './components/Pagination';

// Modals
import UserFormModal from './components/modals/UserFormModal';
import PointsManagementModal from './components/modals/PointsManagementModal';
import VouchersManagementModal from './components/modals/VouchersManagementModal';
import InviteCodeModal from './components/modals/InviteCodeModal';
import VendorCertificateModal from '../../../components/admin/VendorCertificateModal';

// Invite Codes Section
import InviteCodesSection from './components/invite-codes/InviteCodesSection';

// Custom Hooks
import useUsers from './hooks/useUsers';
import usePoints from './hooks/usePoints';
import useVouchers from './hooks/useVouchers';
import useInviteCodes from './hooks/useInviteCodes';
import useUserFilters from './hooks/useUserFilters';

// Constants
import { ROLE_OPTIONS, STATUS_OPTIONS, SORT_OPTIONS } from './constants/userOptions';
import { PERMISSION_OPTIONS } from './constants/permissionOptions';
import { SKILLS_OPTIONS } from './constants/skillsOptions';

// Utils
import { processSkillsArray } from './utils/userHelpers';

/**
 * UserManagement Component
 * Main orchestrator component that manages the entire user management page
 * Handles all user operations, filtering, modals, and navigation between tabs
 */
const UserManagement = () => {
  const { user } = useAuth();

  // ===== Custom Hooks =====
  const {
    users,
    loading,
    totalPages,
    fetchUsers,
    createUser,
    updateUser,
    deleteUser,
    updateUserStatus,
    updateAgentStatus,
    updateAgentCommission
  } = useUsers();

  const {
    searchQuery,
    setSearchQuery,
    debouncedSearch,
    selectedRole,
    setSelectedRole,
    selectedStatus,
    setSelectedStatus,
    sortBy,
    setSortBy,
    currentPage,
    setCurrentPage,
    viewMode,
    setViewMode
  } = useUserFilters();

  const {
    pointsModalUser,
    pointsHistory,
    pointsForm,
    pointsLoading,
    adjustingPoints,
    showPointsModal,
    openPointsModal,
    closePointsModal,
    adjustUserPoints,
    updatePointsForm
  } = usePoints();

  const {
    vouchersModalUser,
    userVouchers,
    vouchersStats,
    vouchersLoading,
    selectedVouchers,
    showVouchersModal,
    openVouchersModal,
    closeVouchersModal,
    deleteVoucher,
    bulkDeleteVouchers,
    cleanupVouchers,
    toggleVoucherSelection
  } = useVouchers();

  const {
    inviteCodes,
    inviteCodeForm,
    showCreateInviteCode,
    setShowCreateInviteCode,
    fetchInviteCodes,
    generateInviteCode,
    updateInviteCodeForm,
    resetInviteCodeForm
  } = useInviteCodes();

  // ===== Local State =====
  const [stats, setStats] = useState({});
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showInviteCodes, setShowInviteCodes] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [certificateModalUser, setCertificateModalUser] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    phone: '',
    city: '',
    country: 'Singapore',
    role: 'customer',
    skills: [],
    experience: 0,
    hourlyRate: 0,
    permissions: [],
    isSuper: false,
    status: 'ACTIVE',
    tacEnabled: false
  });

  // ===== Effects =====

  // Fetch users when filters change
  useEffect(() => {
    if (user?.role === 'admin') {
      fetchUsers({
        page: currentPage,
        selectedRole,
        selectedStatus,
        debouncedSearch
      });
      fetchStats();
    }
  }, [user, currentPage, selectedRole, selectedStatus, debouncedSearch]);

  // Fetch invite codes when tab is shown
  useEffect(() => {
    if (showInviteCodes) {
      fetchInviteCodes();
    }
  }, [showInviteCodes]);

  // ===== Data Fetching Functions =====

  /**
   * Fetch system statistics
   */
  const fetchStats = async () => {
    try {
      const response = await fetch('http://localhost:5000/api/admin/stats', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // ===== User Management Functions =====

  /**
   * Handle creating a new user
   */
  const handleCreateUser = async (e) => {
    e.preventDefault();
    const result = await createUser(formData);
    if (result.success) {
      await fetchUsers({
        page: currentPage,
        selectedRole,
        selectedStatus,
        debouncedSearch
      });
      await fetchStats();
      resetForm();
    }
  };

  /**
   * Handle editing an existing user
   */
  const handleEditUser = async (e) => {
    e.preventDefault();
    const result = await updateUser(editingUser._id, formData);
    if (result.success) {
      await fetchUsers({
        page: currentPage,
        selectedRole,
        selectedStatus,
        debouncedSearch
      });
      await fetchStats();
      resetForm();
    }
  };

  /**
   * Start editing a user
   */
  const startEditUser = (userData) => {
    console.log('🔵 startEditUser called with userData:', userData);
    console.log('🔵 Setting editingUser state...');
    setEditingUser(userData);

    console.log('🔵 Setting form data...');
    setFormData({
      firstName: userData.firstName || '',
      lastName: userData.lastName || '',
      email: userData.email || '',
      password: '', // Don't pre-fill password for security
      phone: userData.phone || '',
      city: userData.city || '',
      country: userData.country || 'Singapore',
      role: userData.role || 'customer',
      skills: processSkillsArray(userData.skills || []),
      experience: userData.experience || 0,
      hourlyRate: userData.hourlyRate || 0,
      permissions: userData.permissions || [],
      isSuper: userData.isSuper || false,
      status: userData.status || 'ACTIVE',
      tacEnabled: userData.tacEnabled || false
    });

    console.log('🔵 Opening form modal...');
    setShowCreateForm(true);
    console.log('🔵 startEditUser completed');
  };

  /**
   * Handle deleting a user
   */
  const handleDeleteUser = async (userId) => {
    const result = await deleteUser(userId);
    if (result.success) {
      await fetchUsers({
        page: currentPage,
        selectedRole,
        selectedStatus,
        debouncedSearch
      });
      await fetchStats();
    }
  };

  /**
   * Handle status change
   */
  const handleStatusChange = async (userId, newStatus) => {
    const result = await updateUserStatus(userId, newStatus);
    if (result.success) {
      await fetchUsers({
        page: currentPage,
        selectedRole,
        selectedStatus,
        debouncedSearch
      });
      await fetchStats();
    }
  };

  /**
   * Handle agent updates (status/commission)
   */
  const handleUpdateAgent = async ({ userId, type, value }) => {
    let result;
    if (type === 'status') {
      result = await updateAgentStatus(userId, value);
    } else if (type === 'commission') {
      result = await updateAgentCommission(userId, value);
    }

    if (result?.success) {
      await fetchUsers({
        page: currentPage,
        selectedRole,
        selectedStatus,
        debouncedSearch
      });
    }
  };

  /**
   * Handle bulk actions
   */
  const handleBulkAction = async (action) => {
    if (selectedUsers.length === 0) {
      toast.error('Please select users first');
      return;
    }

    const confirmMessage = `Are you sure you want to ${action} ${selectedUsers.length} user(s)?`;
    if (!window.confirm(confirmMessage)) return;

    try {
      const promises = selectedUsers.map(userId => {
        if (action === 'activate') {
          return updateUserStatus(userId, 'ACTIVE');
        } else if (action === 'suspend') {
          return updateUserStatus(userId, 'SUSPENDED');
        }
        return Promise.resolve();
      });

      await Promise.all(promises);
      setSelectedUsers([]);
      toast.success(`Bulk ${action} completed successfully!`);

      await fetchUsers({
        page: currentPage,
        selectedRole,
        selectedStatus,
        debouncedSearch
      });
      await fetchStats();
    } catch (error) {
      toast.error(`Failed to ${action} users`);
    }
  };

  /**
   * Reset form and close modal
   */
  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      phone: '',
      city: '',
      country: 'Singapore',
      role: 'customer',
      skills: [],
      experience: 0,
      hourlyRate: 0,
      permissions: [],
      isSuper: false,
      status: 'ACTIVE',
      tacEnabled: false
    });
    setShowCreateForm(false);
    setEditingUser(null);
  };

  /**
   * Handle form input changes
   */
  const handleInputChange = (e) => {
    // Handle both event objects and direct data objects
    if (e && e.target) {
      // Traditional event object
      const { name, value, type, checked } = e.target;
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    } else if (e && typeof e === 'object') {
      // Direct formData object from modal
      setFormData(e);
    }
  };

  /**
   * Handle permission changes
   */
  const handlePermissionChange = (permission) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter(p => p !== permission)
        : [...prev.permissions, permission]
    }));
  };

  /**
   * Handle skill changes
   */
  const handleSkillChange = (skill) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.includes(skill)
        ? prev.skills.filter(s => s !== skill)
        : [...prev.skills, skill]
    }));
  };

  // ===== Filter Handlers =====

  const handleSearch = (query) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  const handleFilter = (type, value) => {
    if (type === 'role') setSelectedRole(value);
    if (type === 'status') setSelectedStatus(value);
    setCurrentPage(1);
  };

  const handleRefresh = async () => {
    await fetchUsers({
      page: currentPage,
      selectedRole,
      selectedStatus,
      debouncedSearch
    });
    await fetchStats();
    toast.success('Data refreshed');
  };

  // ===== Points Management =====

  const handleOpenPointsModal = (userData) => {
    openPointsModal(userData);
  };

  const handleAdjustPoints = async (e) => {
    const result = await adjustUserPoints(e);
    if (result?.success) {
      // Refresh users list to show updated points
      await fetchUsers({
        page: currentPage,
        selectedRole,
        selectedStatus,
        debouncedSearch
      });
    }
  };

  // ===== Vouchers Management =====

  const handleOpenVouchersModal = (userData) => {
    openVouchersModal(userData);
  };

  // ===== Invite Code Management =====

  const handleGenerateInviteCode = async () => {
    const result = await generateInviteCode();
    if (result?.success) {
      await fetchInviteCodes();
    }
  };

  // ===== Render Guards =====

  // Check if user has admin access
  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">Admin privileges required to access this page.</p>
        </div>
      </div>
    );
  }

  // ===== Main Render =====

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Admin Page Banner */}
      <AdminPageBanner
        title="User Management"
        subtitle="Manage users, approve vendors, and monitor system activity"
        icon={Users}
        iconLabel="Admin Panel"
        actions={
          <>
            {stats.pendingUsers > 0 && (
              <button
                onClick={() => {
                  setSelectedStatus('PENDING');
                  setSelectedRole('vendor');
                  setCurrentPage(1);
                }}
                className="flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm text-white border border-white/30 rounded-lg hover:bg-white/30 transition-colors"
              >
                <Clock className="w-4 h-4 mr-2" />
                View Pending ({stats.pendingUsers})
              </button>
            )}

            <button
              onClick={handleRefresh}
              className="flex items-center px-4 py-2 bg-white text-orange-600 rounded-lg hover:bg-orange-50 transition-colors"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </button>

            {user.isSuper && (
              <button
                onClick={() => setShowCreateForm(true)}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create User
              </button>
            )}
          </>
        }
      />

      {/* Tabs Navigation */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            <button
              onClick={() => setShowInviteCodes(false)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                !showInviteCodes
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Users className="inline w-4 h-4 mr-2" />
              Users ({users.length})
            </button>
            <button
              onClick={() => setShowInviteCodes(true)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                showInviteCodes
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Gift className="inline w-4 h-4 mr-2" />
              Invite Codes ({inviteCodes.length})
            </button>
          </nav>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!showInviteCodes ? (
          <>
            {/* Users Tab */}

            {/* Statistics Cards */}
            <UserStatsCards stats={stats} />

            {/* Search and Filters */}
            <SearchAndFilters
              searchQuery={searchQuery}
              onSearch={handleSearch}
              selectedRole={selectedRole}
              selectedStatus={selectedStatus}
              sortBy={sortBy}
              viewMode={viewMode}
              onRoleChange={(value) => handleFilter('role', value)}
              onStatusChange={(value) => handleFilter('status', value)}
              onSortChange={setSortBy}
              onViewModeChange={setViewMode}
              roleOptions={ROLE_OPTIONS}
              statusOptions={STATUS_OPTIONS}
              sortOptions={SORT_OPTIONS}
              selectedUsersCount={selectedUsers.length}
              onBulkAction={handleBulkAction}
            />

            {/* Users Grid/List */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No users found</h3>
                <p className="text-gray-500">Try adjusting your filters or search query.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                <AnimatePresence mode="popLayout">
                  {users.map((userData) => (
                    <UserCard
                      key={userData._id}
                      userData={userData}
                      onEdit={startEditUser}
                      onDelete={handleDeleteUser}
                      onStatusChange={handleStatusChange}
                      onOpenPoints={handleOpenPointsModal}
                      onOpenVouchers={handleOpenVouchersModal}
                      onUpdateAgent={handleUpdateAgent}
                      onViewCertificates={setCertificateModalUser}
                      isSuper={user.isSuper}
                    />
                  ))}
                </AnimatePresence>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            )}
          </>
        ) : (
          <>
            {/* Invite Codes Tab */}
            <InviteCodesSection
              inviteCodes={inviteCodes}
              onGenerateCode={() => setShowCreateInviteCode(true)}
              onRefresh={fetchInviteCodes}
            />
          </>
        )}
      </div>

      {/* Modals */}

      {/* User Form Modal (Create/Edit) */}
      <UserFormModal
        isOpen={showCreateForm}
        onClose={resetForm}
        onSubmit={editingUser ? handleEditUser : handleCreateUser}
        formData={formData}
        editingUser={editingUser}
        onChange={handleInputChange}
        roleOptions={ROLE_OPTIONS}
        statusOptions={STATUS_OPTIONS}
        permissionOptions={PERMISSION_OPTIONS}
        skillsOptions={SKILLS_OPTIONS}
      />

      {/* Points Management Modal */}
      <PointsManagementModal
        show={showPointsModal}
        onClose={closePointsModal}
        user={pointsModalUser}
        pointsHistory={pointsHistory}
        pointsForm={pointsForm}
        onFormChange={updatePointsForm}
        onSubmit={handleAdjustPoints}
        loading={pointsLoading}
        adjusting={adjustingPoints}
      />

      {/* Vouchers Management Modal */}
      <VouchersManagementModal
        show={showVouchersModal}
        onClose={closeVouchersModal}
        user={vouchersModalUser}
        vouchers={userVouchers}
        stats={vouchersStats}
        loading={vouchersLoading}
        selectedVouchers={selectedVouchers}
        onToggleSelection={toggleVoucherSelection}
        onDeleteVoucher={deleteVoucher}
        onBulkDelete={bulkDeleteVouchers}
        onCleanup={cleanupVouchers}
      />

      {/* Invite Code Modal */}
      <InviteCodeModal
        show={showCreateInviteCode}
        onClose={() => {
          setShowCreateInviteCode(false);
          resetInviteCodeForm();
        }}
        formData={inviteCodeForm}
        onFormChange={updateInviteCodeForm}
        onSubmit={handleGenerateInviteCode}
      />

      {/* Vendor Certificate Modal */}
      {certificateModalUser && (
        <VendorCertificateModal
          user={certificateModalUser}
          onClose={() => setCertificateModalUser(null)}
        />
      )}
    </div>
  );
};

export default UserManagement;
